import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { AdPlatformConnections } from "@/components/AdPlatformConnections";
import { WastedSpendAudit } from "@/components/WastedSpendAudit";
import { BudgetGroups } from "@/components/BudgetGroups";
import { CampaignAgent } from "@/components/CampaignAgent";
import { CampaignHierarchy } from "@/components/CampaignHierarchy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  Filter, 
  ChevronDown,
  ArrowUpDown,
  Eye,
  CheckCircle,
  RefreshCw,
  Bot,
  Archive,
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
function authHeaders() { const t = localStorage.getItem("roas_session_token"); return t ? { "Authorization": `Bearer ${t}` } : {}; }

// Platform icon helper
const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  switch (platform) {
    case "meta":
      return <FaMeta className={cn(className, "text-[#1877F2]")} />;
    case "google":
      return <FcGoogle className={className} />;
    case "bing":
      return <FaMicrosoft className={cn(className, "text-[#00A4EF]")} />;
    case "linkedin":
      return <FaLinkedin className={cn(className, "text-[#0A66C2]")} />;
    default:
      return <FaMeta className={cn(className, "text-[#1877F2]")} />;
  }
};

export default function Campaigns({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "roas", direction: "desc" });
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [archivedCampaigns, setArchivedCampaigns] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [bgRefresh, setBgRefresh] = useState(0);
  const [justConnected, setJustConnected] = useState(null);

  const isDemo = user?.is_demo === true;

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch(`${API}/campaigns`, {
        credentials: "include",
      });
      if (response.ok) {
        setCampaigns(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArchivedCampaigns = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/campaigns/archived`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setArchivedCampaigns(await resp.json());
    } catch (e) { console.error("Error:", e); }
  }, []);

  const autoSyncIfStale = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/campaigns/auto-sync`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.synced) {
          const ok = (data.results || []).filter((r) => r.success);
          const total = ok.reduce((s, r) => s + (r.campaigns || 0), 0);
          if (total > 0) toast.success(`Auto-synced ${total} campaigns from your ad platforms`);
          fetchCampaigns();
        }
      }
    } catch (e) { logger.error("Background auto-sync failed:", e); }
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchCampaigns();
    fetchArchivedCampaigns();
    if (!isDemo) autoSyncIfStale();
  }, [fetchCampaigns, fetchArchivedCampaigns, autoSyncIfStale, isDemo]);

  // Handle OAuth redirect from ad platform connection (runs on mount + when params change)
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth_success");
    const platform = searchParams.get("platform");
    if (oauthSuccess === "true" && platform) {
      const label = { linkedin: "LinkedIn Ads", meta: "Meta Ads", google: "Google Ads", bing: "Bing Ads" }[platform] || platform;
      toast.success(`${label} connected! Syncing campaigns…`);
      setJustConnected(platform);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleArchive = async (campaignId) => {
    try {
      const resp = await fetch(`${API}/campaigns/${campaignId}/archive`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        toast.success("Campaign archived");
        fetchCampaigns();
        fetchArchivedCampaigns();
      }
    } catch { toast.error("Failed to archive"); }
  };

  const handleUnarchive = async (campaignId) => {
    try {
      const resp = await fetch(`${API}/campaigns/${campaignId}/unarchive`, { method: "POST", credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        toast.success("Campaign restored");
        fetchCampaigns();
        fetchArchivedCampaigns();
      }
    } catch { toast.error("Failed to restore"); }
  };

  const syncAllCampaigns = async () => {
    if (isDemo) {
      toast.error("Campaign sync is not available in Demo Mode");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await fetch(`${API}/campaigns/sync-hierarchy`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const result = await response.json();
        const details = (result.results || []).filter(r => r.success).map(r =>
          `${r.platform}: ${r.campaigns} campaigns, ${r.ad_groups} ad groups, ${r.ads} ads`
        ).join(" | ");
        const failed = (result.results || []).filter(r => !r.success);
        if (details) {
          toast.success(`Synced: ${details}`);
        } else if (failed.length > 0) {
          toast.error(`Sync failed: ${failed.map(f => `${f.platform}: ${f.error}`).join(", ")}`);
        } else {
          toast.info("No campaigns found on connected platforms");
        }
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to sync campaigns");
      }
    } catch (error) {
      toast.error("Failed to sync campaigns");
    } finally {
      setSyncing(false);
    }
  };

  const handleAgentCampaignsCreated = (newCampaigns) => {
    setCampaigns(prev => [...newCampaigns, ...prev]);
  };

  const handleCreateCampaign = () => {
    if (isDemo) {
      toast.error("Campaign creation is disabled in Demo Mode. Please sign up for full access.");
      return;
    }
    setAgentDialogOpen(true);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  // Filter and sort campaigns
  let filteredCampaigns = campaigns.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = platformFilter === "all" || c.platform === platformFilter;
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesPlatform && matchesStatus;
  });

  filteredCampaigns.sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    const direction = sortConfig.direction === "asc" ? 1 : -1;
    return aValue > bValue ? direction : -direction;
  });

  const getRoasColor = (roas) => {
    if (roas >= 1.75) return "text-emerald-400";
    if (roas >= 1.4) return "text-amber-400";
    return "text-red-400";
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div data-testid="campaigns-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-white font-bold mb-2">
              Campaigns
            </h1>
            <p className="text-zinc-400">
              Manage and monitor all your advertising campaigns
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Sync Button - Live Mode Only */}
            {!isDemo && (
              <Button
                data-testid="sync-campaigns-btn"
                onClick={syncAllCampaigns}
                disabled={syncing}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 font-semibold rounded-lg px-5 py-2.5"
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", syncing && "animate-spin")} />
                {syncing ? "Syncing..." : "Sync Campaigns"}
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row gap-4"
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              data-testid="search-campaigns"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-black/50 border-white/10 text-white hover:bg-white/5">
                <Filter className="w-4 h-4 mr-2" />
                Platform: {platformFilter === "all" ? "All" : platformFilter === "meta" ? "Meta" : "Google"}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#18181B] border-white/10">
              <DropdownMenuItem onClick={() => setPlatformFilter("all")} className="text-white hover:bg-white/10">
                All Platforms
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPlatformFilter("meta")} className="text-white hover:bg-white/10">
                <FaMeta className="w-4 h-4 mr-2 text-[#1877F2]" /> Meta
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPlatformFilter("google")} className="text-white hover:bg-white/10">
                <FcGoogle className="w-4 h-4 mr-2" /> Google
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPlatformFilter("bing")} className="text-white hover:bg-white/10">
                <FaMicrosoft className="w-4 h-4 mr-2 text-[#00A4EF]" /> Bing
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPlatformFilter("linkedin")} className="text-white hover:bg-white/10">
                <FaLinkedin className="w-4 h-4 mr-2 text-[#0A66C2]" /> LinkedIn
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-black/50 border-white/10 text-white hover:bg-white/5">
                Status: {statusFilter === "all" ? "All" : statusFilter}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#18181B] border-white/10">
              <DropdownMenuItem onClick={() => setStatusFilter("all")} className="text-white hover:bg-white/10">
                All Status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter("active")} className="text-white hover:bg-white/10">
                Active
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter("paused")} className="text-white hover:bg-white/10">
                Paused
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>

        {/* Ad Platform Connections */}
        <AdPlatformConnections user={user} onConnectionChange={fetchCampaigns} justConnected={justConnected} />

        {/* Wasted Spend Audit */}
        <WastedSpendAudit onFixApplied={(type) => { fetchCampaigns(); if (type === "group") setBgRefresh((k) => k + 1); }} />

        {/* Budget Groups */}
        <BudgetGroups campaigns={campaigns} refreshSignal={bgRefresh} />

        {/* Campaign Hierarchy */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {filteredCampaigns.length > 0 ? (
            <div className="space-y-2">
              {filteredCampaigns.map((campaign) => (
                <CampaignHierarchy key={campaign.campaign_id} campaign={campaign} onArchive={handleArchive} />
              ))}
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/10 rounded-xl py-12 text-center">
              {isDemo ? (
                <>
                  <p className="text-zinc-400 mb-2">Demo Mode - Viewing sample campaigns</p>
                  <p className="text-zinc-500 text-sm">Sign up for a full account to manage real campaigns</p>
                </>
              ) : (
                <>
                  <p className="text-zinc-400 mb-2">No campaigns synced yet</p>
                  <p className="text-zinc-500 text-sm mb-4">Connect your ad platforms and click Sync to download your campaigns</p>
                  <div className="flex items-center justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={syncAllCampaigns}
                      disabled={syncing}
                      className="bg-black/50 border-white/10 text-white hover:bg-white/5"
                    >
                      <RefreshCw className={cn("w-4 h-4 mr-2", syncing && "animate-spin")} />
                      {syncing ? "Syncing..." : "Sync Campaigns"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>

        {/* Summary */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-between text-sm text-zinc-400"
        >
          <span>Showing {filteredCampaigns.length} of {campaigns.length} campaigns</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              {campaigns.filter(c => c.status === "active").length} Active
            </span>
            {archivedCampaigns.length > 0 && (
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-1 text-zinc-400 hover:text-zinc-300 transition-colors"
              >
                <Archive className="w-4 h-4" />
                {archivedCampaigns.length} Archived
              </button>
            )}
          </div>
        </motion.div>

        {/* Archived Campaigns */}
        <AnimatePresence>
          {showArchived && archivedCampaigns.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="border border-white/10 rounded-xl bg-[#0A0A0A] p-4 space-y-2">
                <h3 className="text-zinc-400 text-sm font-medium flex items-center gap-2 mb-3">
                  <Archive className="w-4 h-4" />
                  Archived Campaigns
                </h3>
                {archivedCampaigns.map((c) => (
                  <div key={c.campaign_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <PlatformIcon platform={c.platform} className="w-4 h-4" />
                      <span className="text-zinc-400 text-sm">{c.name}</span>
                      <Badge className="bg-zinc-700/50 text-zinc-400 text-xs">Archived</Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnarchive(c.campaign_id)}
                      className="text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                    >
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Campaign Agent Dialog */}
      <CampaignAgent 
        open={agentDialogOpen} 
        onOpenChange={setAgentDialogOpen}
        onCampaignsCreated={handleAgentCampaignsCreated}
        user={user}
      />
    </DashboardLayout>
  );
}
